import type {ReactNode} from 'react'
import {View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {Button} from '#/components/Button'
import {Text} from '#/components/Typography'

/**
 * Where a piece of work goes, asked where it can still be answered.
 *
 * Two kinds of work are published into a classroom — a capsule, and a FlashBook —
 * and each is published *from its own studio*, by its own publisher, speaking its
 * own words about what it just made. What they share is not the copy: it is the
 * question, and the fact that the two answers are the difference between one piece
 * of work and another. A capsule for one room and a capsule for a profile are two
 * different capsules; so are two books.
 *
 * Which is why the question lives here rather than in either dialog. Both studios
 * ask it, both must ask it *identically* — a reader who learned the choice in one
 * of them should recognise it in the other — and a second hand-written copy is a
 * second thing to keep in step, which is the mistake this codebase keeps naming.
 * The words about the work itself stay with the publisher that knows the work:
 * this component is handed them.
 *
 * It is only ever rendered by a studio that was opened from a classroom, because
 * a publisher with nowhere to send work but a profile has no question to ask.
 */
export function ClassroomAudienceStep({
  testIDPrefix,
  question,
  publicTitle,
  publicDescription,
  classroomTitle,
  classroomDescription,
  note,
  forClassroom,
  onChange,
}: {
  /** `capsuleAudience` / `flashbookAudience`, so each dialog keeps its own hooks. */
  testIDPrefix: string
  question: string
  /** The answer that means "where everything else goes". */
  publicTitle: string
  publicDescription: string
  /** The answer that means "this room, and nowhere else". */
  classroomTitle: string
  classroomDescription: string
  /** Said under the classroom answer, when it is the one selected. */
  note?: ReactNode
  forClassroom: boolean
  onChange: (forClassroom: boolean) => void
}) {
  const t = useTheme()

  return (
    <View testID={testIDPrefix} style={[a.gap_sm]}>
      <Text style={[a.text_md, a.font_bold]}>{question}</Text>
      <AudienceChoice
        testID={`${testIDPrefix}Public`}
        title={publicTitle}
        description={publicDescription}
        selected={!forClassroom}
        onPress={() => onChange(false)}
      />
      <AudienceChoice
        testID={`${testIDPrefix}Classroom`}
        title={classroomTitle}
        description={classroomDescription}
        selected={forClassroom}
        onPress={() => onChange(true)}
      />
      {forClassroom && note ? (
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>{note}</Text>
      ) : null}
    </View>
  )
}

/**
 * One answer to "where does this go?".
 *
 * A card and not a pill, because the two answers are not two labels: each one
 * changes where the work ends up, and a creator choosing between *my profile* and
 * *just this classroom* is choosing between two different pieces of work.
 */
function AudienceChoice({
  testID,
  title,
  description,
  selected,
  onPress,
}: {
  testID: string
  title: string
  description: string
  selected: boolean
  onPress: () => void
}) {
  const t = useTheme()
  return (
    <Button testID={testID} label={title} onPress={onPress} style={[a.w_full]}>
      {({hovered, focused, pressed}) => (
        <View
          style={[
            a.w_full,
            a.p_md,
            a.rounded_md,
            a.border,
            a.gap_2xs,
            t.atoms.bg_contrast_25,
            {
              borderWidth: 2,
              borderColor: selected
                ? t.palette.primary_500
                : (t.atoms.border_contrast_low.borderColor as string),
            },
            (hovered || focused || pressed) && !selected
              ? t.atoms.border_contrast_high
              : null,
          ]}>
          <Text style={[a.text_md, a.font_bold, a.leading_snug]}>{title}</Text>
          <Text
            style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
            {description}
          </Text>
        </View>
      )}
    </Button>
  )
}
